import { ChangeEvent, FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { Stack } from '@mui/material'
import isEmail from 'validator/lib/isEmail'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUpdateReceiptEmail } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Whether user me is directly updated on value change (default true) */
  directlyUpdateUserMe?: boolean
}

/**
 * @component Displays editable input prefilled with email from users profile. Email can be saved with included button.
 * @example
 *  <ReceiptEmail
 *    onValueChange={(value) => setValueInSomeExternalState(value)}
 *    onValidityChange={(isValid) => setValidityInSomeExternalState(validity)}
 *  />
 */
export const ReceiptEmail: FC<Props> = ({
  directlyUpdateUserMe = true,
}) => {

  const { t } = useTranslation(['receipt_email_input', 'request_status'])

  const { clientData } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const userEmail = useMemo(() => clientData?.billingEmail || '', [clientData])

  const updateEmailMutation = useUpdateReceiptEmail()

  const inputRef = useRef<HTMLInputElement>(null)

  const [isEmailInputTouched, setIsEmailInputTouched] = useState<boolean>(false)
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true)
  const [value, setValue] = useState<string>('')

  const errorMessage = useMemo(() => {
    if (!updateEmailMutation.isError && !isEmailValid && isEmailInputTouched) {
      return t('invalid_error')
    }

    if (updateEmailMutation.isError) {
      return t('save_error')
    }
    return undefined
  }, [isEmailInputTouched, isEmailValid, t, updateEmailMutation.isError])

  const handleValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // purge state if request already fired to remove icon/error
    if (updateEmailMutation.isSuccess || updateEmailMutation.isError) updateEmailMutation.reset()

    if (!isEmail(e.target.value.trim())) {
    }
    setIsEmailValid(isEmail(e.target.value.trim()) || (e.target.value.trim() === ''))

    setValue(e.target.value)
  }, [updateEmailMutation])

  // Prefill email with saved one
  useLayoutEffect(() => {
    if (!isEmailInputTouched && userEmail) {
      setValue(userEmail)
      setIsEmailInputTouched(true)
    }
  }, [isEmailInputTouched, userEmail])

  // Purge updateReceiptEmail state on page leave
  useEffect(() => {
    return () => {
      updateEmailMutation.reset()
    }
    // Only trigger once on load otherwise goes to infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack width="100%" gap={1.2} ref={inputRef}>

      <MUIInputField
        label={t('label')}
        placeholder={t('placeholder')}
        value={value}
        errorText={errorMessage}
        isError={!!errorMessage}
        disabled={updateEmailMutation.isPending}
        onChange={handleValueChange}
        onBlur={() => {
          setIsEmailInputTouched(true)
        }}
      />

      {userEmail !== value && !updateEmailMutation.isPending &&
        <MUIButton
          type="secondaryBorder"
          disabled={!isEmailValid}
          isLoading={updateEmailMutation.isPending}
          onClick={() =>
            updateEmailMutation.mutate(
              { email: value, callUpdateUserMe: directlyUpdateUserMe },
              {
                onSuccess: () => {
                  spawnSuccessToast(t('success'))
                },
                onError: () => {
                  spawnErrorToast(t('save_error'))
                }
              }
            )
          }
          sx={{ width: 'fit-content' }}
        >
          {t('cta')}
        </MUIButton>
      }

    </Stack>
  )
}
